import type { PageBox, PageMargins } from "@norkron/web-docz";
import type { PageConfig } from "@norkron/web-page-layout";

/**
 * `canvasPageMargins` — page margins as a DOCUMENT property.
 *
 * WHAT CHANGED. Margins used to be a build-time constant: `canvasPageConfig.ts`
 * spread the Cerebr8 plate `Body` geometry and nothing could override it, so the
 * only way to change a document's margins was to edit and redeploy the app. They
 * are now carried per-document on the SAME `SectionDecoration` carrier that
 * already holds page colour / page borders / line numbering (`@norkron/web-docz`
 * `pageMargins`), and this module is the two pure pieces the canvas needs:
 *
 *   · {@link pageConfigWithMargins} — overlay a document's margin box onto the
 *     page template, overriding ONLY the four margin fields.
 *   · {@link CEREBR8_PAGE_BOX} — the sheet the margins picker validates against,
 *     including the bands the plate reserves for its running elements.
 *
 * WHY IT IS A SEPARATE MODULE from `canvasPageConfig.ts`. That file answers "what
 * is the page template?" and is a constant by design; this one answers "what did
 * this document ask for on top of it?". Keeping the override out of the template
 * keeps the template auditable — the plate geometry is still spread from ONE
 * place and cannot drift, and a document with no margin set still lays out
 * byte-identically to before margins existed.
 *
 * WHY NO ENGINE CHANGE WAS NEEDED. `pl_layout`'s V0 header has carried
 * `margin_top/right/bottom/left` as `u16`s since the beginning
 * (`pdfz/src/wasm/layout_pipeline.zig` `PageConfig`, bytes 6..14, mirrored by
 * `web-page-layout`'s `encodePlInput`). The margins were always a wire field the
 * consumer simply never varied — so this feature passes a value THROUGH the
 * existing wire rather than changing `A4_DEFAULT` or the engine's defaults.
 */

/**
 * Overlay a document's margin box onto a page template.
 *
 * `undefined`/`null` margins mean "inherit the page master" and return the base
 * config unchanged — so a document that has never had margins set is laid out on
 * exactly the bytes it was laid out on before this feature existed.
 *
 * Only the four margin fields are touched. That matters more than it looks:
 * `canvasPageConfig.ts` pins `justify` and `glyphGranular` because they are what
 * give every line its `(block_ord, byte_offset)` caret provenance. A merge that
 * rebuilt the config from scratch could drop them and silently disable the caret.
 */
export function pageConfigWithMargins(
  base: PageConfig,
  margins: PageMargins | null | undefined,
): PageConfig {
  if (margins == null) return base;
  return {
    ...base,
    marginTopPt: margins.topPt,
    marginRightPt: margins.rightPt,
    marginBottomPt: margins.bottomPt,
    marginLeftPt: margins.leftPt,
  };
}

/**
 * The sheet an elaborate document is laid out on, as the margins picker sees it.
 *
 * The two BANDS are the load-bearing part, and they are the honest answer to
 * "what happens to the reserved band if a user overrides the top margin?".
 *
 * The plate (`src/render/cerebr8.plate.zon`) paints a running header and a
 * running footer into the top and bottom bands of every body page:
 *
 *   · header — `.box = .{ .min_height_pt = 26, .margins = .{ .bottom = 8 } }`
 *     → it needs **34pt** of top margin to sit in.
 *   · footer — `.min_height_pt = 18` on body pages, but **40pt** on the first
 *     page (`Prepared for editorial review` / `CONFIDENTIAL`). The larger one is
 *     the binding constraint, so the floor is **40pt**.
 *
 * The canvas cannot paint running elements at all (the PLCF wire carries no
 * running-element channel), so on screen those bands render as empty space — the
 * `CANVAS_MARGIN_TOP_PT = 99` trade-off documented in `canvasPageConfig.ts`. But
 * the EXPORTED PDF does paint them. So a top margin below 34pt does not merely
 * look tight in the export: the running header is drawn ON TOP OF the first
 * lines of body text. That is why these floors are a REFUSAL rather than a hint,
 * and why they live here (with the plate) instead of in `web-docz`, which knows
 * nothing about Cerebr8's page master.
 *
 * A user may still tighten the margins well below the plate's 99/72 — they just
 * cannot take them inside a band that something else is already painting into.
 */
export const CEREBR8_PAGE_BOX: PageBox = {
  widthPt: 595,
  heightPt: 842,
  headerBandPt: 34,
  footerBandPt: 40,
};
